
import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    CircularProgress,
    Alert,
    Stack,
    useTheme,
    IconButton
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import DeleteIcon from '@mui/icons-material/Delete';

const PdfConverter = () => {
    const theme = useTheme();
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [dragActive, setDragActive] = useState(false);

    const processFile = (selectedFile) => {
        setError('');
        setSuccess('');

        // Simple client-side check, backend does robust check
        const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'text/plain'];
        if (!validTypes.includes(selectedFile.type)) {
            setError('Please upload an Image (PNG, JPG) or Text file.');
            return;
        }
        if (selectedFile.size > 10 * 1024 * 1024) {
            setError('File size exceeds 10MB limit.');
            return;
        }

        setFile(selectedFile);
    };

    const handleFileChange = (event) => {
        if (event.target.files[0]) processFile(event.target.files[0]);
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
        else if (e.type === 'dragleave') setDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files[0]) processFile(e.dataTransfer.files[0]);
    };

    const handleConvert = async () => {
        if (!file) return;

        setLoading(true);
        setError('');
        setSuccess('');

        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await fetch('/api/convert-to-pdf', {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to convert file');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${file.name.split('.')[0]}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            a.remove();

            setSuccess('File converted successfully!');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
            <Typography variant="h4" sx={{ mb: 1, fontWeight: 700, color: theme.palette.text.primary }}>
                PDF Converter
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Convert Images or Text files to PDF instantly.
            </Typography>

            <Paper
                elevation={0}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                sx={{
                    p: 4,
                    border: `2px dashed ${dragActive ? theme.palette.primary.main : theme.palette.divider}`,
                    borderRadius: 4,
                    textAlign: 'center',
                    bgcolor: dragActive ? (theme.palette.mode === 'dark' ? 'rgba(0, 242, 255, 0.05)' : 'rgba(0, 120, 255, 0.05)') : theme.palette.background.default,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    minHeight: 200,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
                onClick={() => document.getElementById('convert-upload').click()}
            >
                <input
                    accept="image/*,text/plain"
                    style={{ display: 'none' }}
                    id="convert-upload"
                    type="file"
                    onChange={handleFileChange}
                />

                {file ? (
                    <Stack direction="row" alignItems="center" spacing={2} sx={{
                        p: 2,
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                        borderRadius: 2,
                        width: '100%',
                        maxWidth: 400
                    }}>
                        <DescriptionIcon sx={{ fontSize: 40, color: 'info.main' }} />
                        <Box sx={{ flexGrow: 1, textAlign: 'left', overflow: 'hidden' }}>
                            <Typography variant="subtitle1" noWrap>{file.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {(file.size / 1024).toFixed(1)} KB
                            </Typography>
                        </Box>
                        <IconButton size="small" onClick={(e) => {
                            e.stopPropagation();
                            setFile(null);
                        }}>
                            <DeleteIcon />
                        </IconButton>
                    </Stack>
                ) : (
                    <Stack alignItems="center" spacing={2}>
                        <CloudUploadIcon sx={{ fontSize: 64, color: dragActive ? 'primary.main' : 'text.secondary', opacity: dragActive ? 1 : 0.5 }} />
                        <Typography variant="h6">
                            {dragActive ? "Drop File here" : "Click or Drag Image/Text here"}
                        </Typography>
                    </Stack>
                )}
            </Paper>

            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}

            {file && (
                <Box sx={{ mt: 4 }}>
                    <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdfIcon />}
                        onClick={handleConvert}
                        disabled={loading}
                    >
                        {loading ? 'Converting...' : 'Convert to PDF'}
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default PdfConverter;
